export interface WorkshopVideoHighlight {
  title: string;
  url: string;
}

export type WorkshopHighlight = string | WorkshopVideoHighlight;

export interface Workshop {
  id: string;
  slug: string;
  title: string;
  /** Short summary for listing cards */
  shortDescription: string;
  /** Full page copy; omit for legacy list-only workshops */
  fullDescription?: {
    paragraphs: string[];
    bullets: string[];
    closing: string;
  };
  date: string;
  dateTimeIso: string;
  endDateTimeIso: string;
  category?: string;
  isPast: boolean;
  speaker?: string;
  speakerTitle?: string;
  featuredImage?: string;
  videoHighlights?: WorkshopHighlight[];
  registrationLink?: string;
  linkedInEventUrl?: string;
  /** When set, LinkedIn is a short public clip, not the full replay. */
  linkedInPublicTrimMinutes?: number;
  youtubeLiveUrl?: string;
  seoDescription?: string;
  ogImage?: string;
}

export const isTitledHighlight = (
  item: WorkshopHighlight,
): item is WorkshopVideoHighlight => typeof item !== "string";

export const getTitledHighlights = (
  highlights: WorkshopHighlight[] | undefined,
): WorkshopVideoHighlight[] =>
  (highlights ?? []).filter(isTitledHighlight);

export const getUntitledHighlightUrls = (
  highlights: WorkshopHighlight[] | undefined,
): string[] =>
  (highlights ?? []).filter((item): item is string => typeof item === "string");

export const WORKSHOPS: Workshop[] = [
  {
    id: "redesign-work-with-ai-sep-2026",
    slug: "redesign-work-with-ai-sep-2026",
    title: "Redesign work with AI",
    shortDescription:
      "Most job descriptions still describe work from before AI showed up. HR bolts on a line about \"comfort with AI tools\" and calls it done. That is a sticker on old work, not redesign. Start with you and your work. Let AI take the constraints off. Break the role into tasks. Ask where AI actually fits. Rewrite the work around judgment, not throughput. Free 60-minute workshop. Hands-on. Leave with a method you can reuse Monday.",
    fullDescription: {
      paragraphs: [
        "Most job descriptions still describe work from before AI showed up. HR bolts on a line about \"comfort with AI tools\" and calls it done. That is not redesign. That is a sticker on old work.",
        "Work design starts with the work: tasks, constraints, resources. AI lets you take constraints off so the work can be rebuilt around judgment, not throughput, not so the old job just gets faster. Here is the thing: AI does not perform equally across tasks. Structured, easy-to-check work is a different fit than judgment, relationships, or sensitive people data. Redesign means deciding, task by task, which is which. That gives you a clearer split: what the human uniquely owns, where AI takes a well-defined task with human review, and where AI only prepares the human who still has to walk in the room. Not a replacement story. The human is still the one who walks into the room. This 60-minute session opens with what actually changes when a role is built this way, then goes hands-on. Zoom seats run the exercise live. Livestream viewers leave with the method and a worked example.",
      ],
      bullets: [
        "Break a role into tasks and test which ones actually fit AI today",
        "Map those tasks: AI for you, with you, or preparing you",
        "Rewrite the role so AI is in the work, not a sticker on the JD",
      ],
      closing:
        "Leave knowing how to redesign the work around judgment, not throughput, with AI in the work, not a sticker on the posting.",
    },
    date: "Thursday, September 17, 2026 · 12:00–1:00 PM ET",
    dateTimeIso: "2026-09-17T12:00:00-04:00",
    endDateTimeIso: "2026-09-17T13:00:00-04:00",
    category: "Education",
    isPast: false,
    speaker: "Jonathan Flower & Lindsay Hansen",
    speakerTitle: "Workplace Labs",
    registrationLink:
      "https://members.aiforhrmastermind.com/share/o/T2NjdXJyZW5jZTozNzUwMTE2Mzk4NjA0NTk3Mzg4",
    linkedInEventUrl: "https://www.linkedin.com/events/7500255897097732096",
    youtubeLiveUrl: "https://www.youtube.com/watch?v=DXHtbXWkC6E",
    featuredImage: "/images/redesign-work-with-ai-workshop.webp",
    seoDescription:
      "Free 60-minute HR workshop on September 17. Start with you and your work. Let AI take the constraints off. Rewrite work around judgment, not throughput. Watch on LinkedIn Live or YouTube, or register for interactive Zoom.",
    ogImage: "/images/redesign-work-with-ai-workshop.webp",
  },
  {
    id: "start-your-personal-ai-toolkit-aug-2026",
    slug: "start-your-personal-ai-toolkit-aug-2026",
    title: "Start Your Personal AI Toolkit",
    shortDescription:
      "A 60-minute workshop on reusable AI guidance for the work you do again and again: instructions that help AI work with you more effectively.",
    fullDescription: {
      paragraphs: [
        "AI is not consistently useful out of the box. It can help you think through a complex challenge one day, then give you a generic answer that misses the point the next. The real opportunity is Human + AI. AI will never have your judgment, taste, passion, or courage. Those stay human. What AI can bring is execution power. Give it the right context about your role, priorities, preferences, and what you are trying to accomplish, and it can work harder and more effectively for you.",
        "In this practical 60-minute workshop, we began building a personal AI toolkit for the work you do again and again. Participants created reusable guidance that helps AI work with you more effectively, tested it on a real work scenario, and learned how to improve the result instead of starting over.",
      ],
      bullets: [
        "Showed how the right context can make AI's help more useful and consistent.",
        "Built reusable instructions that show AI how you work and what useful support looks like.",
        "Put those instructions to work on a real challenge and improved the result live.",
        "Took questions, compared approaches, and troubleshot as we built together.",
      ],
      closing:
        "Attendees left with the foundation of their own toolkit: at least one reusable asset they can use immediately.",
    },
    date: "Friday, August 14, 2026 · 12:00–1:00 PM ET",
    dateTimeIso: "2026-08-14T16:00:00-04:00",
    endDateTimeIso: "2026-08-14T17:00:00-04:00",
    category: "Education",
    isPast: true,
    registrationLink:
      "https://members.aiforhrmastermind.com/share/o/T2NjdXJyZW5jZTozNzQ1MDYyMDkwOTg3MDY5NzU5",
    linkedInEventUrl: "https://www.linkedin.com/events/7490150711733780480",
    linkedInPublicTrimMinutes: 3,
    youtubeLiveUrl: "https://www.youtube.com/watch?v=65JLOQeVBsY",
    featuredImage: "/images/start-your-personal-ai-toolkit-workshop.webp",
    videoHighlights: [
      {
        title: "AI memory and learning explained",
        url: "https://video.aiforhrmastermind.com/public/default/start-your-personal-ai-toolkit-ai-memory-and-learning-explained.mp4",
      },
      {
        title: "AI's jagged frontier",
        url: "https://video.aiforhrmastermind.com/public/default/start-your-personal-ai-toolkit-ais-jagged-frontier.mp4",
      },
      {
        title: "AI selects your expert persona",
        url: "https://video.aiforhrmastermind.com/public/default/start-your-personal-ai-toolkit-ai-selects-your-expert-persona.mp4",
      },
    ],
    seoDescription:
      "Highlight clips from the August 14, 2026 workshop on building a personal AI toolkit for recurring HR work. Full recording for paying members. October 2026 cohort starts October 1.",
    ogImage: "/images/start-your-personal-ai-toolkit-workshop.webp",
  },
  {
    id: "just-use-ai-for-that-jul-2026",
    slug: "just-use-ai-for-that-jul-2026",
    title: 'How Am I Supposed to "Just Use AI for That?"',
    shortDescription:
      'Your boss said "just use AI for that." For what, exactly? Jonathan Flower and Lindsay Hansen led a 60-minute lunch-and-learn on realistic AI boundaries for HR: live demos with honest recovery, tool-agnostic prompt patterns, and the questions to ask when leadership hands you a vague mandate. The full recording is available to paying Mastermind members.',
    fullDescription: {
      paragraphs: [
        'Leadership says "just use AI for that" and walks away. You are left holding a vague mandate, a tool you have barely used, and a real HR problem that still needs solving. This session was built for that exact moment: what AI can genuinely do for HR work today, where it falls down, and how to tell the difference before you stake your credibility on an output.',
        "Jonathan Flower and Lindsay Hansen ran a 60-minute lunch-and-learn with live, unscripted demos. When a prompt missed, they showed the recovery instead of cutting away — because knowing how to steer a bad first answer is the skill that actually transfers back to your desk.",
      ],
      bullets: [
        "Set realistic boundaries for what AI should and should not touch in HR work.",
        "Watch live demos, including the misses and how to recover from them.",
        "Learn tool-agnostic prompt patterns that work in ChatGPT, Claude, or Gemini.",
        "Get the questions to ask leadership when the AI mandate is vague.",
      ],
      closing:
        "You will leave able to translate a vague AI directive into a specific, scoped HR use case you can actually deliver on.",
    },
    date: "July 9, 2026 · 12:00 PM ET",
    dateTimeIso: "2026-07-09T16:00:00-04:00",
    endDateTimeIso: "2026-07-09T17:00:00-04:00",
    isPast: true,
    speaker: "Jonathan Flower & Lindsay Hansen",
    speakerTitle: "Workplace Labs",
    featuredImage: "/images/just-use-ai-workshop.webp",
    registrationLink:
      "https://members.aiforhrmastermind.com/share/o/T2NjdXJyZW5jZTozNzQwMTM5NzkxNzA3NzgyMjMw",
    linkedInEventUrl:
      "https://www.linkedin.com/events/7480267302157012993?viewAsMember=true",
    youtubeLiveUrl: "https://youtube.com/live/ojguLdddrlo",
    videoHighlights: [
      "https://video.aiforhrmastermind.com/public/ai-for-hr-mastermind/AI_expert_personas_and_voice_interview_tips.mp4",
      "https://video.aiforhrmastermind.com/public/ai-for-hr-mastermind/AI_resume_summarization_without_bias.mp4",
      "https://video.aiforhrmastermind.com/public/ai-for-hr-mastermind/Five-minute_lie_AI_burnout_fixes.mp4",
    ],
    seoDescription:
      'When leadership says "just use AI for that," what do you actually do? Watch this HR lunch-and-learn on realistic AI boundaries, live demos, and tool-agnostic prompt patterns for HR teams.',
  },
  {
    id: "vibe-code-hr-ai-assistant",
    slug: "vibe-code-hr-ai-assistant",
    title: "Build Your AI Onboarding Assistant in Lovable (No Code Required)",
    shortDescription:
      "Turn your employee handbook into an AI assistant that answers new hire questions 24/7—without writing a single line of code. In this 60-minute hands-on workshop, Lindsay Hansen walked through building a working prototype you can demo immediately.",
    fullDescription: {
      paragraphs: [
        "New hires ask the same handbook questions over and over, and every one of them lands in an HR inbox. An AI onboarding assistant grounded in your own policy documents answers those questions instantly, consistently, and at any hour — and you do not need an engineer or a budget line to build the first version.",
        "In this 60-minute hands-on workshop, Lindsay Hansen and Jonathan Flower built a working onboarding assistant live in Lovable, starting from a plain employee handbook. Attendees followed along in their own accounts and finished with a prototype they could demo to their leadership the same week.",
      ],
      bullets: [
        "Turn an existing employee handbook into a grounded knowledge source for AI.",
        "Build a working assistant in Lovable with no code and no engineering support.",
        "Test the assistant against real new-hire questions and tighten weak answers.",
        "Learn what to check before putting an HR assistant in front of employees.",
      ],
      closing:
        "You will leave with a working onboarding assistant prototype and a clear path to demo it internally.",
    },
    date: "January 28, 2025",
    dateTimeIso: "2025-01-28T17:00:00-05:00",
    endDateTimeIso: "2025-01-28T18:00:00-05:00",
    isPast: true,
    speaker: "Lindsay Hansen & Jonathan Flower",
    speakerTitle: "Organizational Architecture & AI Enablement Leaders",
    featuredImage: "/images/vibe-code-workshop.webp",
    registrationLink:
      "https://members.aiforhrmastermind.com/share/o/T2NjdXJyZW5jZTozNjk4NTE0MDM5NzQwMzE3MzI0",
    seoDescription:
      "Turn your employee handbook into an AI onboarding assistant that answers new hire questions 24/7 — no code required. A hands-on HR workshop built live in Lovable.",
  },
  {
    id: "human-centered-ai-talent-strategy",
    slug: "human-centered-ai-talent-strategy",
    title: "Human-Centered AI for Talent Strategy",
    shortDescription:
      "Human insight meets AI innovation: making HR agile and impactful. Alexia Saphos and Jonathan Flower led an interactive discussion on bringing AI into talent strategy without losing the human touch.",
    fullDescription: {
      paragraphs: [
        "Talent strategy runs on judgment — reading a team, weighing a trade-off, knowing when a candidate is worth a second look. None of that transfers to a model. What does transfer is the slow, repetitive work around it: job analysis, competency drafting, summarizing feedback, keeping documentation current. That is where AI earns its place in HR.",
        "Alexia Saphos and Jonathan Flower led an interactive discussion on how technology can accelerate talent strategies while keeping people at the center, with practical ways to bring AI into existing HR workflows for greater agility and impact.",
      ],
      bullets: [
        "Separate the parts of talent strategy that need human judgment from the parts AI can accelerate.",
        "See practical AI entry points across job analysis, competencies, and candidate workflows.",
        "Understand the fairness and transparency questions to answer before you scale a use case.",
        "Take away a starting point for your own human-centered AI talent workflow.",
      ],
      closing:
        "You will leave with a clear view of where AI belongs in your talent strategy — and where it does not.",
    },
    date: "December 10, 2024",
    dateTimeIso: "2024-12-10T17:00:00-05:00",
    endDateTimeIso: "2024-12-10T18:00:00-05:00",
    isPast: true,
    speaker: "Alexia Saphos & Jonathan Flower",
    featuredImage: "/images/human-centered-ai-workshop.webp",
    registrationLink:
      "https://members.aiforhrmastermind.com/share/o/T2NjdXJyZW5jZTozNjk3NDEzNzMxODg3Nzg2MDEx",
    videoHighlights: [
      "https://video.aiforhrmastermind.com/public/ai-for-hr-mastermind/How_AI_turned_an_HR_skeptic_into_a_strategic_partn%20(1).mp4",
      "https://video.aiforhrmastermind.com/public/ai-for-hr-mastermind/Build_a_chain_of_GPT_agents_that_convert_job_analy.mp4",
      "https://video.aiforhrmastermind.com/public/ai-for-hr-mastermind/Treat_AI_like_an_intern_how_to_use_AI_to_build_spe.mp4",
    ],
    seoDescription:
      "How AI can accelerate talent strategy without losing the human touch. An interactive HR workshop on practical, human-centered AI use cases across the talent lifecycle.",
  },
];

export const getWorkshopBySlug = (slug: string): Workshop | undefined =>
  WORKSHOPS.find((workshop) => workshop.slug === slug);

export const getWorkshopPath = (slug: string): string => `/workshops/${slug}`;

export const UPCOMING_WORKSHOPS = WORKSHOPS.filter((workshop) => !workshop.isPast);
export const PAST_WORKSHOPS = WORKSHOPS.filter((workshop) => workshop.isPast);

/** Every workshop with a full write-up gets its own indexable detail page. */
export const WORKSHOP_INDEXABLE_PATHS = WORKSHOPS.filter(
  (workshop) => workshop.fullDescription,
).map((workshop) => getWorkshopPath(workshop.slug));

